import React, { Suspense, lazy, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import ThemeToggle from '../ui/ThemeToggle';
import { useAppContext } from '../../hooks/useAppContext';
import { UserRole } from '../../types';

const ForgotPasswordModal = lazy(() => import('./ForgotPasswordModal'));

const adminLoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type AdminLoginFormData = z.infer<typeof adminLoginSchema>;

const ADMIN_ROLES = [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER, UserRole.ACCOUNTANT];

const AdminLoginPage: React.FC = () => {
  const { login, logout, isLoading, addNotification } = useAppContext();
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginFormData>({
    resolver: zodResolver(adminLoginSchema)
  });

  const onSubmit = async (data: AdminLoginFormData) => {
    const user = await login(data.email, data.password);
    if (!user) return;

    if (!ADMIN_ROLES.includes(user.role)) {
      addNotification('This login is reserved for AGM admin accounts only.', 'error');
      await logout();
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.12),_transparent_38%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] dark:bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.15),_transparent_36%),linear-gradient(180deg,_#020617_0%,_#0f172a_100%)]">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 overflow-hidden rounded-[2rem] border border-white/70 bg-white/90 shadow-[0_30px_80px_-35px_rgba(15,23,42,0.35)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/90 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="relative overflow-hidden bg-slate-950 px-6 py-10 text-white sm:px-10 lg:px-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.45),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.18),_transparent_30%)]" />
            <div className="relative">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[11px] font-black uppercase tracking-[0.28em] text-indigo-200">
                AGM Admin Console
              </div>
              <h1 className="mt-8 max-w-xl text-4xl font-black tracking-tight sm:text-5xl">
                Secure access for AGM operations teams.
              </h1>
              <p className="mt-5 max-w-lg text-sm leading-7 text-slate-300 sm:text-base">
                Use this page only for AGM-administered accounts. Customer organizations should sign in from the main website.
              </p>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-indigo-200">Admin Access</p>
                  <p className="mt-3 text-sm text-slate-200">Platform admin, super admin, manager, and accountant roles only.</p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-indigo-200">Customer Access</p>
                  <p className="mt-3 text-sm text-slate-200">Customer organizations now sign in from the main site at the root domain.</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => { window.location.href = '/'; }}
                className="mt-10 text-sm font-bold text-white/80 underline decoration-white/20 underline-offset-4 transition hover:text-white"
              >
                Back to main website
              </button>
            </div>
          </div>

          <div className="px-6 py-10 sm:px-10 lg:px-12">
            <div className="mx-auto max-w-md">
              <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                <p className="text-[11px] font-black uppercase tracking-[0.26em] text-indigo-500">Admin Sign In</p>
                <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 dark:text-slate-100">Welcome back</h2>
                <p className="mt-3 text-sm leading-7 text-slate-500 dark:text-slate-400">
                  Sign in with your AGM-administered account to access operations, fulfillment, billing, and portal controls.
                </p>
                </div>
                <ThemeToggle compact />
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input
                  label="Admin Email"
                  type="email"
                  id="admin-login-email"
                  placeholder="admin@agm.com"
                  error={errors.email?.message}
                  className="text-indigo-950 font-bold"
                  {...register('email')}
                />
                <div>
                  <Input
                    label="Secure Password"
                    type="password"
                    id="admin-login-password"
                    placeholder="••••••••••••"
                    error={errors.password?.message}
                    className="text-indigo-950 font-bold"
                    {...register('password')}
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-600 transition hover:text-indigo-800"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full border-none py-4 text-sm font-black uppercase tracking-[0.24em] shadow-[0_16px_40px_-16px_rgba(79,70,229,0.55)]"
                >
                  {isLoading ? 'Verifying...' : 'Enter Admin Console'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          onSwitchToLogin={() => setShowForgotPassword(false)}
        />
      </Suspense>
    </div>
  );
};

export default AdminLoginPage;
